import { Link, useNavigate } from 'react-router-dom'
import { formatDayTime, formatMoney, formatPhone } from '@dorixona/utils'
import { Icon } from '../../ui/Icon'
import type { Order } from './api'
import { STATUS_TONE, pointsPayText, shipText, statusText } from './model'

export interface OrdersTableProps {
  orders: Order[]
  /** Отбор сужен — в пустой таблице предлагаем его сбросить. */
  filtered: boolean
  page: number
  onReset: () => void
  onFirstPage: () => void
  /** Идёт новая выборка, а видна прежняя. */
  busy?: boolean
}

/**
 * Все заказы списком — по образцу каталога: номер-ссылка, клиент с телефоном,
 * статус тоном, способ получения меткой, сумма и дата. Открыть заказ — по
 * строке или по номеру (ссылка работает и с клавиатуры).
 */
export function OrdersTable({
  orders,
  filtered,
  page,
  onReset,
  onFirstPage,
  busy = false,
}: OrdersTableProps) {
  const navigate = useNavigate()

  return (
    <div className="ui-table-wrap" aria-busy={busy}>
      <table className="ui-table ord-table">
        <colgroup>
          <col className="w-num" />
          <col />
          <col className="w-status" />
          <col className="w-method" />
          <col className="w-sum" />
          <col className="w-date" />
        </colgroup>
        <thead>
          <tr>
            <th scope="col">Номер</th>
            <th scope="col">Клиент</th>
            <th scope="col">Статус</th>
            <th scope="col">Получение</th>
            <th scope="col" data-align="end">
              Сумма
            </th>
            <th scope="col">Создан</th>
          </tr>
        </thead>
        <tbody>
          {orders.length === 0 ? (
            <tr>
              <td colSpan={6}>
                <div className="ui-empty">
                  <Icon name="i-search" />
                  <b>Заказов не найдено</b>
                  <p>
                    {page > 1
                      ? `На странице ${page} заказов нет — список стал короче.`
                      : filtered
                        ? 'Измените запрос или сбросьте фильтры.'
                        : 'Заказы появятся здесь, как только клиенты их оформят.'}
                  </p>
                  {filtered ? (
                    <button
                      type="button"
                      className="ui-btn"
                      data-size="sm"
                      data-variant="secondary"
                      onClick={onReset}
                    >
                      Сбросить фильтры
                    </button>
                  ) : null}
                  {page > 1 ? (
                    <button
                      type="button"
                      className="ui-btn"
                      data-size="sm"
                      data-variant="secondary"
                      onClick={onFirstPage}
                    >
                      К первой странице
                    </button>
                  ) : null}
                </div>
              </td>
            </tr>
          ) : null}
          {orders.map((order) => {
            const href = `/orders/${order.id}`
            return (
              <tr
                key={order.id}
                data-href={href}
                onClick={() => {
                  void navigate(href)
                }}
              >
                <td>
                  <Link
                    to={href}
                    className="primary num"
                    aria-label={`Открыть заказ № ${order.number}`}
                    onClick={(event) => {
                      event.stopPropagation()
                    }}
                  >
                    <span className="ord-no">№</span>
                    {order.number}
                  </Link>
                </td>
                <td>
                  {order.customerName || '—'}
                  {order.customerPhone ? (
                    <span className="secondary num">{formatPhone(order.customerPhone)}</span>
                  ) : null}
                </td>
                <td>
                  <span className="ui-status" data-tone={STATUS_TONE[order.status]}>
                    {statusText(order.status)}
                  </span>
                </td>
                <td>
                  <span className="ui-method" data-method={order.ship}>
                    <Icon name={order.ship === 'delivery' ? 'i-truck' : 'i-pin'} size={14} />
                    {shipText(order.ship)}
                  </span>
                </td>
                <td data-align="end">
                  {formatMoney(order.total)}
                  {order.spend ? <span className="secondary">{pointsPayText(order)}</span> : null}
                </td>
                <td className="num ord-muted">{formatDayTime(order.createdAt)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
